import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

const getUrlLanguage = () => {
    const params = new URLSearchParams(window.location.search);
    const langParam = params.get('lang')?.toLowerCase();
    if (!langParam) return null;

    // Use the main language map to resolve the URL parameter
    const languageMap = {
        nl: 'nederlands',
        de: 'deutsch',
        es: 'spanish',
        fr: 'french',
        sr: 'serbian',
        ro: 'romanian',
        pt: 'portuguese',
        ar: 'arabic',
        no: 'norwegian',
        'no-no': 'norwegian',
        befr: 'be-fr',
        'be-fr': 'be-fr',
        benl: 'be-nl',
        'be-nl': 'be-nl',
        pl: 'polish',
        'pl-pl': 'polish',
        da: 'danish',
        'da-dk': 'danish',
        dansk: 'danish',
        danish: 'danish',
        sv: 'swedish',
        'sv-se': 'swedish',
        svenska: 'swedish',
        swedish: 'swedish',
        sl: 'slovenian',
        si: 'slovenian',
        'sl-si': 'slovenian',
        slovenian: 'slovenian',
        el: 'greek',
        'el-cy': 'cyprus',
        'el-gr': 'greek',
        cy: 'cyprus',
        'cy-cy': 'cyprus',
        cyprus: 'cyprus',
        greek: 'greek',
        'bh-ar': 'bh-ar',
        'bh-en': 'bh-en',
        'bahrain-arabic': 'bh-ar',
        'bahrain-english': 'bh-en',
        'uae-ar': 'uae-arabic',
        'uae-en': 'uae-english',
        'uae-arabic': 'uae-arabic',
        'uae-english': 'uae-english',
        'sa-ar': 'sa-arabic',
        'sa-en': 'sa-english',
        'sa-arabic': 'sa-arabic',
        'sa-english': 'sa-english',
        'kw-ar': 'kw-arabic',
        'kw-en': 'kw-english',
        'kw-arabic': 'kw-arabic',
        'kw-english': 'kw-english',
        'qa-ar': 'qa-arabic',
        'qa-en': 'qa-english',
        'qa-arabic': 'qa-arabic',
        'qa-english': 'qa-english',
        'om-ar': 'om-arabic',
        'om-en': 'om-english',
        'om-arabic': 'om-arabic',
        'om-english': 'om-english',
        'iq-ar': 'iq-arabic',
        'iq-en': 'iq-english',
        'iq-arabic': 'iq-arabic',
        'iq-english': 'iq-english',
        'jo-ar': 'jo-arabic',
        'jo-en': 'jo-english',
        'jo-arabic': 'jo-arabic',
        'jo-english': 'jo-english',
        'lb-ar': 'lb-arabic',
        'lb-en': 'lb-english',
        'lb-arabic': 'lb-arabic',
        'lb-english': 'lb-english',
        'sy-ar': 'sy-arabic',
        'sy-en': 'sy-english',
        'sy-arabic': 'sy-arabic',
        'sy-english': 'sy-english',
        'ps-ar': 'ps-arabic',
        'ps-en': 'ps-english',
        'ps-arabic': 'ps-arabic',
        'ps-english': 'ps-english',
        'ye-ar': 'ye-arabic',
        'ye-en': 'ye-english',
        'ye-arabic': 'ye-arabic',
        'ye-english': 'ye-english',
        'eg-ar': 'eg-arabic',
        'eg-en': 'eg-english',
        'eg-arabic': 'eg-arabic',
        'eg-english': 'eg-english',
        // Add any other language codes and their corresponding names here
        // Example: 'it': 'italian'
    };

    // Direct match for the parameter (e.g., lang=fr)
    if (languageMap[langParam]) {
        return languageMap[langParam];
    }

    // Check if the parameter is exactly 'be-fr' or 'be-nl'
    if (langParam === 'be-fr') {
        return 'be-fr';
    }
    if (langParam === 'be-nl') {
        return 'be-nl';
    }

    // Check if the parameter starts with a known key (e.g., lang=fr-fr maps to french)
    const foundEntry = Object.entries(languageMap).find(([key]) => langParam.startsWith(key));
    if (foundEntry) {
        return foundEntry[1]; // Return the language name (e.g., 'french')
    }

    return null; // Return null if no match found
};

const getBrowserLanguage = () => {
    const browserLang = navigator.language.toLowerCase();

    // Special case for Belgian French and Dutch
    if (browserLang === 'be-fr' || browserLang === 'fr-be') {
        return 'be-fr';
    }
    if (browserLang === 'be-nl' || browserLang === 'nl-be') {
        return 'be-nl';
    }

    // Special case for Bahrain
    if (browserLang === 'bh-ar' || browserLang === 'ar-bh') {
        return 'bh-ar';
    }
    if (browserLang === 'bh-en' || browserLang === 'en-bh') {
        return 'bh-en';
    }
    if (browserLang === 'bahrain-arabic') {
        return 'bh-ar';
    }
    if (browserLang === 'bahrain-english') {
        return 'bh-en';
    }

    // Special case for UAE
    if (browserLang === 'uae-ar' || browserLang === 'ar-ae') {
        return 'uae-arabic';
    }
    if (browserLang === 'uae-en' || browserLang === 'en-ae') {
        return 'uae-english';
    }
    if (browserLang === 'uae-arabic') {
        return 'uae-arabic';
    }
    if (browserLang === 'uae-english') {
        return 'uae-english';
    }

    // Special cases for other Arabic countries
    const arabicCountryMapping = {
        'sa-ar': 'sa-arabic', 'ar-sa': 'sa-arabic', 'sa-arabic': 'sa-arabic',
        'sa-en': 'sa-english', 'en-sa': 'sa-english', 'sa-english': 'sa-english',
        'kw-ar': 'kw-arabic', 'ar-kw': 'kw-arabic', 'kw-arabic': 'kw-arabic',
        'kw-en': 'kw-english', 'en-kw': 'kw-english', 'kw-english': 'kw-english',
        'qa-ar': 'qa-arabic', 'ar-qa': 'qa-arabic', 'qa-arabic': 'qa-arabic',
        'qa-en': 'qa-english', 'en-qa': 'qa-english', 'qa-english': 'qa-english',
        'om-ar': 'om-arabic', 'ar-om': 'om-arabic', 'om-arabic': 'om-arabic',
        'om-en': 'om-english', 'en-om': 'om-english', 'om-english': 'om-english',
        'iq-ar': 'iq-arabic', 'ar-iq': 'iq-arabic', 'iq-arabic': 'iq-arabic',
        'iq-en': 'iq-english', 'en-iq': 'iq-english', 'iq-english': 'iq-english',
        'jo-ar': 'jo-arabic', 'ar-jo': 'jo-arabic', 'jo-arabic': 'jo-arabic',
        'jo-en': 'jo-english', 'en-jo': 'jo-english', 'jo-english': 'jo-english',
        'lb-ar': 'lb-arabic', 'ar-lb': 'lb-arabic', 'lb-arabic': 'lb-arabic',
        'lb-en': 'lb-english', 'en-lb': 'lb-english', 'lb-english': 'lb-english',
        'sy-ar': 'sy-arabic', 'ar-sy': 'sy-arabic', 'sy-arabic': 'sy-arabic',
        'sy-en': 'sy-english', 'en-sy': 'sy-english', 'sy-english': 'sy-english',
        'ps-ar': 'ps-arabic', 'ar-ps': 'ps-arabic', 'ps-arabic': 'ps-arabic',
        'ps-en': 'ps-english', 'en-ps': 'ps-english', 'ps-english': 'ps-english',
        'ye-ar': 'ye-arabic', 'ar-ye': 'ye-arabic', 'ye-arabic': 'ye-arabic',
        'ye-en': 'ye-english', 'en-ye': 'ye-english', 'ye-english': 'ye-english',
        'eg-ar': 'eg-arabic', 'ar-eg': 'eg-arabic', 'eg-arabic': 'eg-arabic',
        'eg-en': 'eg-english', 'en-eg': 'eg-english', 'eg-english': 'eg-english'
    };

    if (arabicCountryMapping[browserLang]) {
        return arabicCountryMapping[browserLang];
    }

    const languageMap = {
        nl: 'nederlands',
        de: 'deutsch',
        es: 'spanish',
        fr: 'french',
        sr: 'serbian',
        ro: 'romanian',
        pt: 'portuguese',
        ar: 'arabic',
        no: 'norwegian',
        'no-no': 'norwegian',
        pl: 'polish',
        'pl-pl': 'polish',
        da: 'danish',
        'da-dk': 'danish',
        danish: 'danish',
        sv: 'swedish',
        'sv-se': 'swedish',
        swedish: 'swedish',
        sl: 'slovenian',
        si: 'slovenian',
        'sl-si': 'slovenian',
        slovenian: 'slovenian',
        el: 'greek',
        'el-cy': 'cyprus',
        'el-gr': 'greek',
        cy: 'cyprus',
        'cy-cy': 'cyprus',
        cyprus: 'cyprus',
        greek: 'greek'
    };

    return Object.entries(languageMap).find(([key]) => browserLang.startsWith(key))?.[1] || 'english';
};

const getSavedLanguage = () => localStorage.getItem('userLanguage');
const saveLanguage = (lang) => localStorage.setItem('userLanguage', lang);

export const useLanguage = () => {
    const context = useContext(LanguageContext);
    if (!context) {
        throw new Error('useLanguage must be used within a LanguageProvider');
    }
    return context;
};

export const LanguageProvider = ({ children }) => {
    // Priority: URL Param -> Saved Language -> Browser Language -> Default ('english')
    const [language, setLanguageState] = useState(() => {
        const urlLang = getUrlLanguage();
        if (urlLang) return urlLang;

        const savedLang = getSavedLanguage();
        if (savedLang) return savedLang;

        return getBrowserLanguage();
    });

    useEffect(() => {
        saveLanguage(language);
    }, [language]);

    const setLanguage = (newLanguage) => {
        setLanguageState(newLanguage);
        saveLanguage(newLanguage);
    };

    return (
        <LanguageContext.Provider value={{ language, setLanguage }}>
            {children}
        </LanguageContext.Provider>
    );
};
